/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { SitesFilter, FilterOption } from './types'
import * as etmfApi from '../../../services/api/etmf'
import { useServiceContext } from '../../../root'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect/InfiniteScrollSelect'

interface SitesFilterPanelProps {
  onFilterChange: (fsiIds: number[]) => void
  isCollapsed: boolean
}

const SITES_FILTER_KEY = 'sitesKey'

export const SitesFilterPanel: React.FC<SitesFilterPanelProps> = ({ onFilterChange, isCollapsed }) => {
  const { t } = useTranslation()
  const { service } = useServiceContext()
  const [filter, setFilter] = useState<SitesFilter>({
    mySites: null
  })

  // Load persisted filter on mount
  useEffect(() => {
    const persisted = localStorage.getItem(SITES_FILTER_KEY)
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted)
        setFilter(parsed)
        // Apply persisted filter
        const fsiIds = parsed.mySites?.map((o: FilterOption) => o.value) || []
        if (fsiIds.length > 0) {
          onFilterChange(fsiIds)
        }
      } catch (error) {
        console.error('Failed to parse persisted filter:', error)
      }
    }
  }, [onFilterChange])

  const handleApplyFilter = useCallback(() => {
    // Persist filter
    localStorage.setItem(SITES_FILTER_KEY, JSON.stringify(filter))

    // Extract FSI IDs from selected options and convert to numbers
    const fsiIds = filter.mySites?.map((o) => parseInt(o.value, 10)) || []
    onFilterChange(fsiIds)
  }, [filter, onFilterChange])

  const handleClearFilter = useCallback(() => {
    const clearedFilter: SitesFilter = {
      mySites: null
    }
    setFilter(clearedFilter)
    localStorage.setItem(SITES_FILTER_KEY, JSON.stringify(clearedFilter))
    onFilterChange([])
  }, [onFilterChange])

  const loadSiteOptions = async (search: string, page: number) => {
    const userId = service.auth.getUserId()
    if (!userId) {
      return { options: [], hasMore: false }
    }
    return etmfApi.getOptionsForAsyncPickerInfiniteScrollMySites(search || null, page, userId.toString())
  }

  // Don't render if NOT collapsed (collapsed=true means show in legacy, so !isCollapsed means hide)
  if (!isCollapsed) {
    return null
  }

  return (
    <div className="filter-wrapper">
      <div className="row">
        <div className="col-sm-4">
          <div className="form-group">
            <label>{t('EMTF_MD_SITES')}</label>
            <InfiniteScrollSelect
              placeholder={t('ETMF_MD_SITES_PLACEHOLDER')}
              asyncLoadFn={loadSiteOptions}
              onChange={(options) => setFilter({ ...filter, mySites: options as FilterOption[] })}
              value={filter.mySites || []}
              isMulti
              searchable
            />
          </div>
        </div>

        <div className="col-sm-12">
          <div className="clearfix">
            <div className="pull-left">
              <button
                data-action="filter"
                className="btn btn-primary"
                onClick={handleApplyFilter}
                id="sites-filter-apply-btn"
              >
                {t('BTN_FILTER')}
              </button>
              <button
                data-action="reset"
                className="btn btn-link"
                onClick={handleClearFilter}
                id="sites-filter-clear-btn"
              >
                {t('BTN_CLEAR_FILTER')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
